import { cloneElement, createContext, Fragment, type KeyboardEvent, type ReactElement, type ReactNode, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Dropdown } from "../Dropdown";
import { Loader } from "../Loader";
import { YStack } from "../Stacks";
import { Body2 } from "../Typography";
import { ListboxOption, type ListboxOptionProps } from "./ListboxOption";
import { useListboxKeyboard } from "./useListboxKeyboard";
const firstEnabledIndex = (options: ListboxOption[]): number => options.findIndex(option => !option.disabled);

// Cap the option list (popover only) so a long list scrolls internally instead
// of running down the viewport. This is a list concern, not a surface one — the
// Dropdown itself is unopinionated about height.
const LISTBOX_MAX_HEIGHT = 320;
type ListboxContextValue = {
  options: ListboxOption[];
  activeIndex: number;
  /** Stable id of the `role="listbox"` body, also the trigger's `aria-controls`. */
  listboxId: string;
  /** Accessible name stamped on the listbox body. */
  name: string | undefined;
  /** Multi-select: rows carry a leading checkbox and the body is multiselectable. */
  multiple: boolean;
  /** Selection test that works for both modes (single value or multi set). */
  isSelected: (value: string) => boolean;
  /** Single-select trailing check: Select shows it; Combobox reflects the pick in
   *  its input and opts out. Always off in multi (the leading checkbox replaces it). */
  showSelectedCheck: boolean;
  getOptionId: (index: number) => string;
  onSelectIndex: (index: number) => void;
  onActivateIndex: (index: number) => void;
};
const ListboxContext = createContext<ListboxContextValue | null>(null);
const useListboxContext = (): ListboxContextValue => {
  const context = useContext(ListboxContext);
  if (context === null) {
    throw new Error("Listbox.Options must render inside <Listbox>");
  }
  return context;
};
type ListboxBaseProps = {
  /** The option data; Combobox passes the already-filtered set. */
  options: ListboxOption[];
  /** Lift `open` when the field derives it (Combobox); omit to let Listbox own
   *  it as a simple toggle. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The field's trigger element; Listbox stamps the combobox aria + keyboard
   *  onto it via `cloneElement`, so its props must accept arbitrary attributes. */
  trigger: ReactElement<Record<string, unknown>>;
  /** Combobox mode: Space/Home/End drive the text caret, not the listbox. */
  typeToFilter?: boolean;
  /** `<Listbox.Options/>`. */
  children: ReactNode;
  /** Popover surface only — match the popover width to the trigger. @default true */
  matchTriggerWidth?: boolean;
  /** Sheet surface only — pinned header above the teleported options (e.g. the
   *  Combobox search input). */
  sheetHeader?: ReactNode;
  /** Sheet surface only — open to a fixed tall height and scroll. @default false */
  sheetFullHeight?: boolean;
  /** Explicit id for the `role="listbox"` body, so a field rendering its own
   *  search input (Combobox in a sheet) can point `aria-controls` at it.
   *  Defaults to a generated id. */
  id?: string;
};

// Single vs multi is a discriminated union, not overloaded props: single owns one
// `value`, multi owns a `values` set and toggles without closing.
type ListboxSingleProps = {
  multiple?: false;
  /** Controlled selection — the field owns the value and feeds it down. */
  value: string | null;
  onValueChange: (value: string) => void;
  values?: never;
  onValuesChange?: never;
};
type ListboxMultiProps = {
  multiple: true;
  /** Controlled multi-selection — selecting a row toggles it in/out of this set. */
  values: string[];
  onValuesChange: (values: string[]) => void;
  value?: never;
  onValueChange?: never;
};

// Require at least one accessible name; `aria-label` wins when both are set
// (resolved by `name` below).
export type ListboxProps = ListboxBaseProps & (ListboxSingleProps | ListboxMultiProps) & ({
  label: string;
  "aria-label"?: string;
} | {
  label?: string;
  "aria-label": string;
});

/**
 * The selection brain behind field overlays like Select and Combobox. Owns the
 * active option, option ids, the listbox keyboard, and the combobox aria stamped
 * onto the field's trigger; composes the `Dropdown` surface and renders the
 * `role="listbox"` body via `<Listbox.Options/>`. The field supplies the trigger
 * markup and the selection/filter policy. Single-select by default; pass
 * `multiple` for a toggle-and-keep-open multiselect with leading-checkbox rows.
 */
const ListboxRoot = (props: ListboxProps) => {
  const {
    options,
    open: openProp,
    onOpenChange,
    trigger,
    typeToFilter = false,
    children,
    matchTriggerWidth = true,
    sheetHeader,
    sheetFullHeight = false,
    id,
    label,
    "aria-label": ariaLabel
  } = props;

  // Narrow the selection union once; the locals carry the stable underlying
  // props refs so downstream memos don't churn.
  const multiple = props.multiple === true;
  const values = props.multiple === true ? props.values : undefined;
  const value = props.multiple === true ? undefined : props.value;
  const onValuesChange = props.multiple === true ? props.onValuesChange : undefined;
  const onValueChange = props.multiple === true ? undefined : props.onValueChange;
  const generatedId = useId();
  const listboxId = id ?? generatedId;
  const getOptionId = useCallback((index: number) => `${listboxId}-option-${index}`, [listboxId]);
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const requestOpen = useCallback((next: boolean) => {
    if (onOpenChange !== undefined) {
      onOpenChange(next);
    } else {
      setInternalOpen(next);
    }
  }, [onOpenChange]);
  const [activeIndex, setActiveIndex] = useState(0);

  // Set for O(1) membership: an open multi list tests every row (and seeds the
  // highlight below), so a linear `includes` per row is O(rows x picks) and
  // re-runs on every toggle.
  const valueSet = useMemo(() => new Set(values), [values]);
  const isSelected = useCallback((candidate: string) => multiple ? valueSet.has(candidate) : value === candidate, [multiple, valueSet, value]);
  const selectIndex = useCallback((index: number) => {
    const option = options[index];
    if (!option || option.disabled) {
      return;
    }
    if (multiple) {
      const current = values ?? [];
      const next = current.includes(option.value) ? current.filter(candidate => candidate !== option.value) : [...current, option.value];
      onValuesChange?.(next);
      // Multi toggles and keeps the list open — no close on select.
      return;
    }
    onValueChange?.(option.value);
    // Close only when Listbox owns `open`. A field that lifts `open` owns the
    // close too (via its onValueChange) so the trigger and list stay in sync.
    if (openProp === undefined) {
      setInternalOpen(false);
    }
  }, [multiple, values, onValuesChange, onValueChange, options, openProp]);

  // The index to seed/follow the keyboard highlight: the (first) selected option.
  // Skip disabled rows so a selected-but-disabled option can't trap the highlight
  // on an inert row — the open then falls through to the first-enabled seed.
  const selectedIndex = useMemo(() => multiple ? options.findIndex(option => !option.disabled && valueSet.has(option.value)) : options.findIndex(option => !option.disabled && option.value === value), [multiple, options, valueSet, value]);

  // Non-filtering mode: seed the highlight on open to the selected option (or
  // first enabled). Gated on open's transition via wasOpenRef, so a re-render
  // that only swaps the options ref can't reseed mid-navigation.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (typeToFilter) {
      return;
    }
    if (open && !wasOpenRef.current) {
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstEnabledIndex(options));
    }
    wasOpenRef.current = open;
  }, [typeToFilter, open, selectedIndex, options]);

  // Follow an external value change while the list stays open (single only —
  // a multi toggle must not snap the highlight off the user's arrowed position).
  // Keyed on selectedIndex (not options) so an unrelated options-ref change from
  // a parent re-render can't move the highlight either.
  useEffect(() => {
    if (typeToFilter || multiple) {
      return;
    }
    if (open && selectedIndex >= 0) {
      setActiveIndex(selectedIndex);
    }
  }, [typeToFilter, multiple, open, selectedIndex]);

  // Filtering mode: keep the active row valid as the filtered set changes.
  useEffect(() => {
    if (!typeToFilter) {
      return;
    }
    if (options.length === 0) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex(prev => prev >= 0 && prev < options.length && !options[prev]?.disabled ? prev : firstEnabledIndex(options));
  }, [typeToFilter, options]);
  const {
    onKeyDown: listboxKeyDown,
    activeDescendantId
  } = useListboxKeyboard({
    open,
    itemCount: options.length,
    activeIndex,
    onActiveIndexChange: setActiveIndex,
    onSelect: selectIndex,
    onOpen: () => requestOpen(true),
    onClose: () => requestOpen(false),
    getOptionId,
    isItemDisabled: index => Boolean(options[index]?.disabled)
  });
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    if (typeToFilter) {
      // Filtering mode: Space/Home/End drive the text caret, so let them
      // through — only a non-filtering list claims those keys for navigation.
      if (event.key === " " || event.key === "Home" || event.key === "End") {
        return;
      }
      listboxKeyDown(event);
      return;
    }
    if (event.target !== event.currentTarget) {
      return;
    }
    // Non-filtering mode: Enter/Space open the closed list here (the keyboard
    // hook opens only on the arrow keys); once open, they select via the hook.
    if (!open && (event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      requestOpen(true);
      return;
    }
    listboxKeyDown(event);
  }, [typeToFilter, open, listboxKeyDown, requestOpen]);
  const name = ariaLabel ?? label;

  // Compose, don't clobber: a trigger may carry its own onKeyDown. Run it first
  // and only drive the listbox if it didn't already handle the key.
  const triggerOnKeyDown = trigger.props.onKeyDown;
  const wiredTrigger = cloneElement(trigger, {
    role: "combobox",
    "aria-haspopup": "listbox",
    "aria-expanded": open,
    // Only while open — the listbox isn't mounted when closed, so a permanent
    // aria-controls would dangle.
    "aria-controls": open ? listboxId : undefined,
    "aria-activedescendant": activeDescendantId,
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (typeof triggerOnKeyDown === "function") {
        triggerOnKeyDown(event);
      }
      if (!event.defaultPrevented) {
        handleKeyDown(event);
      }
    }
  });
  const contextValue = useMemo<ListboxContextValue>(() => ({
    options,
    activeIndex,
    listboxId,
    name,
    multiple,
    isSelected,
    showSelectedCheck: !typeToFilter && !multiple,
    getOptionId,
    onSelectIndex: selectIndex,
    onActivateIndex: setActiveIndex
  }), [options, activeIndex, listboxId, name, multiple, isSelected, typeToFilter, getOptionId, selectIndex]);
  return <Dropdown open={open} onOpenChange={requestOpen} trigger={wiredTrigger} matchTriggerWidth={matchTriggerWidth} maxHeight={LISTBOX_MAX_HEIGHT} sheetHeader={sheetHeader} sheetFullHeight={sheetFullHeight} contentLabel={name} aria-activedescendant={activeDescendantId} data-sentry-element="Dropdown" data-sentry-component="ListboxRoot" data-sentry-source-file="Listbox.tsx">
      {/* Provider wraps the options node, not the whole Dropdown: on touch the
          Dropdown teleports this node into a bottom Sheet (Tamagui `Adapt`),
          severing the React parent chain — a provider outside the Dropdown
          wouldn't reach the teleported `Listbox.Options`. The trigger reads no
          context, so scoping it here costs nothing. */}
      <ListboxContext.Provider value={contextValue} data-sentry-element="ListboxContext.Provider" data-sentry-source-file="Listbox.tsx">
        {children}
      </ListboxContext.Provider>
    </Dropdown>;
};
export type ListboxOptionsProps = {
  /** Escape hatch for custom rows; Listbox still owns selection, keyboard, and
   *  ids. Spread `optionProps` onto a `ListboxOption` or your own element. */
  renderOption?: (option: ListboxOption, optionProps: ListboxOptionProps) => ReactNode;
  /** Show the loader in place of rows (async fetch in flight). */
  loading?: boolean;
  /** Show the error message in place of rows (async fetch failed). */
  error?: boolean;
  /** Copy when nothing matches and there's no loading/error state. */
  emptyText?: string;
  /** Copy when an async load fails. */
  errorText?: string;
};

/**
 * The listbox body. Renders the default `ListboxOption` rows (or `renderOption`),
 * with loading/empty/error emitted as siblings *outside* the `role="listbox"`
 * node — a listbox whose children aren't `role="option"` rows reads as malformed
 * to screen readers.
 */
const ListboxOptions = ({
  renderOption,
  loading = false,
  error = false,
  emptyText,
  errorText
}: ListboxOptionsProps) => {
  const {
    options,
    activeIndex,
    listboxId,
    name,
    multiple,
    isSelected,
    showSelectedCheck,
    getOptionId,
    onSelectIndex,
    onActivateIndex
  } = useListboxContext();
  const status = loading ? <YStack role="status" aria-label="Loading options" alignItems="center" paddingVertical="$sp2">
      <Loader size="$iconMedium" color="$iconDefault" />
    </YStack> : error ? <YStack role="alert" paddingVertical="$sp1" paddingHorizontal="$sp1.5">
      <Body2 color="$textNegative">{errorText}</Body2>
    </YStack> : options.length === 0 && emptyText !== undefined ? <YStack paddingVertical="$sp1" paddingHorizontal="$sp1.5">
      <Body2 color="$textSubtle">{emptyText}</Body2>
    </YStack> : null;
  return <>
      {status}
      <YStack id={listboxId} aria-label={name} gap="$sp0.5"
    // role="listbox" on the render-prop div: Tamagui's Role union omits it.
    render={<div role="listbox" aria-multiselectable={multiple ? true : undefined} />} data-sentry-element="YStack" data-sentry-source-file="Listbox.tsx">
        {/* Loading/error show in place of rows — don't render stale options
            underneath the status. */}
        {loading || error ? null : options.map((option, index) => {
        const optionProps: ListboxOptionProps = {
          id: getOptionId(index),
          index,
          label: option.label,
          selected: isSelected(option.value),
          active: index === activeIndex,
          disabled: option.disabled,
          leading: option.icon,
          showSelectedCheck,
          multiple,
          onSelectIndex,
          onActivateIndex
        };
        return renderOption ? <Fragment key={option.value}>
                  {renderOption(option, optionProps)}
                </Fragment> : <ListboxOption key={option.value} {...optionProps} />;
      })}
      </YStack>
    </>;
};
export const Listbox = Object.assign(ListboxRoot, {
  Options: ListboxOptions
});